/**
 * Which coordinate frame a splat source's data is already in.
 *
 * Every format the viewer loaded natively before SPZ support (PLY, compressed PLY,
 * SOG, .splat, LOD manifests) carries 3DGS training output in the COLMAP camera
 * frame: X right, Y DOWN, Z forward. The viewer replicates the BabylonJS
 * GaussianSplattingMesh Y=-1 scale plus a Z handedness flip on those so every
 * hotspot, waypoint and splat transform authored in the Babylon era still lines up.
 *
 * Two sources are already in the PlayCanvas-native right-handed Y-up frame and
 * must SKIP that flip, or they render upside down and Z-mirrored:
 *   - LCC2 converted by splat-transform, flagged through the LOD manifest's
 *     storySplatCoordinateCorrection.sourceFormat === 'lcc2'.
 *   - Niantic SPZ, whose spec stores splats in the RUB (right-up-back) frame.
 *     PlayCanvas's own simple-spz example applies no flip rotation for this reason.
 */

/** True for a `.spz` URL (case-insensitive, ignoring query and fragment). */
export function isSpzSplatUrl(url: string | null | undefined): boolean {
    if (!url) return false;
    const clean = url.toLowerCase().split(/[?#]/)[0];
    return clean.endsWith('.spz');
}

/**
 * True when the data behind `url` is already in the PlayCanvas-native frame, so the
 * legacy Babylon Y/Z flip must not be applied. `correctionSourceFormat` is the LOD
 * manifest's coordinate-correction source, when one was read.
 */
export function usesNativeSplatFrame(
    url: string | null | undefined,
    correctionSourceFormat: string | null | undefined,
): boolean {
    return correctionSourceFormat === 'lcc2' || isSpzSplatUrl(url);
}
